import { useState, useCallback, useEffect } from 'react';
import type { CheckoutAddress, DeliveryDate } from '../../types/checkout';

interface DeliveryDateSectionProps {
  selectedAddress: CheckoutAddress | null;
  deliveryDates: DeliveryDate[];
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  onContinue: () => void;
  // For lazy-loaded dates (fast checkout)
  onLoadDates?: (addressId: number) => Promise<{ success: boolean; dates?: DeliveryDate[]; isDeliverable?: boolean }>;
  isLoading?: boolean;
  isDateCompatible?: (date: string) => boolean;
  discountWarning?: string | null;
}

export function DeliveryDateSection({
  selectedAddress,
  deliveryDates,
  selectedDate,
  onSelectDate,
  onContinue,
  onLoadDates,
  isLoading = false,
  isDateCompatible,
  discountWarning,
}: DeliveryDateSectionProps) {
  const [error, setError] = useState<string | null>(null);
  const [isLoadingDates, setIsLoadingDates] = useState(false);

  // Lazy-load dates when address changes (fast checkout pattern)
  useEffect(() => {
    if (onLoadDates && selectedAddress && deliveryDates.length === 0) {
      setIsLoadingDates(true);
      setError(null);

      onLoadDates(selectedAddress.id)
        .then((result) => {
          if (!result.success) {
            setError('Error al cargar las fechas de entrega');
          } else if (!result.isDeliverable) {
            setError('Esta direccion esta fuera de nuestra zona de entrega');
          }
        })
        .catch(() => {
          setError('Error de conexion');
        })
        .finally(() => {
          setIsLoadingDates(false);
        });
    }
  }, [selectedAddress, deliveryDates.length, onLoadDates]);

  const handleDateSelect = useCallback((date: string) => {
    setError(null);
    onSelectDate(date);
  }, [onSelectDate]);

  const handleContinue = useCallback(() => {
    if (!selectedDate) {
      setError('Selecciona una fecha de entrega');
      return;
    }
    setError(null);
    onContinue();
  }, [selectedDate, onContinue]);

  if (!selectedAddress) {
    return (
      <div>
        <p className="text-sm text-gray-500">
          Primero selecciona una direccion de entrega
        </p>
      </div>
    );
  }

  if (isLoadingDates) {
    return (
      <div className="flex flex-col items-center py-8 text-gray-500">
        <div className="mb-3 h-8 w-8 animate-spin rounded-full border-[3px] border-gray-200 border-t-green-500" />
        <p className="text-sm">Cargando fechas disponibles...</p>
      </div>
    );
  }

  if (error && deliveryDates.length === 0) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3.5 text-center text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (deliveryDates.length === 0) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3.5 text-center text-sm text-red-600">
        No hay fechas de entrega disponibles para esta direccion
      </div>
    );
  }

  return (
    <div>
      <p className="mb-3 text-sm text-gray-500">
        Selecciona cuando quieres recibir tu pedido
      </p>

      <div className="mb-4 flex items-center gap-2 rounded-lg bg-gray-100 px-3.5 py-2.5 text-[13px] text-gray-500">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        {selectedAddress.address}
      </div>

      {discountWarning && selectedDate && (
        <div className="mb-3 flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 p-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 flex-shrink-0 text-amber-500">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p className="text-sm text-amber-800">{discountWarning}</p>
        </div>
      )}

      <div className="mb-4 flex max-h-[280px] flex-col gap-2 overflow-y-auto">
        {deliveryDates.map((date) => {
          const compatible = isDateCompatible ? isDateCompatible(date.value) : true;
          const showWarning = isDateCompatible && !compatible;

          return (
            <button
              key={date.value}
              type="button"
              className={`flex w-full items-start gap-3 rounded-xl border-2 p-3.5 text-left transition-all duration-200
                ${selectedDate === date.value ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'}`}
              onClick={() => handleDateSelect(date.value)}
            >
              <div className="flex-shrink-0 pt-0.5">
                {selectedDate === date.value ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#5cb85c">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="4" fill="white" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-[15px] text-gray-800">{date.label}</span>
                {showWarning && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-amber-600">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    Descuento no aplica en este turno
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-600">
          {error}
        </div>
      )}

      <button
        type="button"
        className="w-full rounded-lg bg-green-500 px-5 py-3.5 text-base font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-300"
        onClick={handleContinue}
        disabled={isLoading || !selectedDate}
      >
        Continuar
      </button>
    </div>
  );
}
